<template>
  <!-- Desktop: Sidebar vertical with timeline -->
  <div v-if="!isMobile" class="wizard-sidebar">
    <nav class="steps-timeline">
      <div
        v-for="(step, index) in steps"
        :key="index"
        class="step-item"
        :class="{
          'step-active': currentStep === index,
          'step-completed': currentStep > index,
          'step-upcoming': currentStep < index,
          'step-clickable': allowClick && currentStep > index
        }"
        :data-testid="`wizard-step-${index}`"
        @click="handleStepClick(index)"
      >
        <div class="step-marker">
          <span v-if="currentStep > index" class="step-check">&#10003;</span>
          <span v-else class="step-number">{{ index + 1 }}</span>
        </div>
        <span class="step-label">{{ step.title }}</span>
      </div>
    </nav>

    <div v-if="showProgress" class="sidebar-progress">
      <div class="progress-text">{{ t('Passo') }} {{ currentStep + 1 }} {{ t('de') }} {{ steps.length }}</div>
      <div class="progress-bar-track">
        <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
      </div>
      <div class="progress-percent">{{ Math.round(progressPercent) }}%</div>
    </div>
  </div>

  <!-- Mobile: Accordion collapsible -->
  <div v-else class="wizard-accordion">
    <button class="accordion-header" data-testid="wizard-accordion-header" @click="accordionOpen = !accordionOpen" :aria-expanded="accordionOpen">
      <div class="accordion-header-left">
        <div v-if="showProgress" class="accordion-progress-mini">
          <div class="progress-bar-track">
            <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
        </div>
        <span class="accordion-step-label">
          {{ currentStep + 1 }}. {{ steps[currentStep]?.title }}
        </span>
        <span class="accordion-step-count">{{ currentStep + 1 }}/{{ steps.length }}</span>
      </div>
      <i class="pi" :class="accordionOpen ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
    </button>

    <Transition name="accordion-slide">
      <div v-if="accordionOpen" class="accordion-body">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="accordion-step-item"
          :class="{
            'step-active': currentStep === index,
            'step-completed': currentStep > index,
            'step-upcoming': currentStep < index,
            'step-clickable': allowClick && currentStep > index
          }"
          :data-testid="`wizard-accordion-step-${index}`"
          @click="handleStepClick(index)"
        >
          <div class="step-marker">
            <span v-if="currentStep > index" class="step-check">&#10003;</span>
            <span v-else class="step-number">{{ index + 1 }}</span>
          </div>
          <span class="step-label">{{ step.title }}</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

interface WizardStep {
  title: string;
  description?: string;
  key?: string;
  [key: string]: any;
}

const { t } = useI18n();

const props = withDefaults(defineProps<{
  steps: WizardStep[];
  currentStep: number;
  showProgress?: boolean;
  allowClick?: boolean;
}>(), {
  showProgress: true,
  allowClick: false,
});

const emit = defineEmits<{
  'step-click': [stepIndex: number];
}>();

const accordionOpen = ref(false);
const isMobile = ref(false);

const progressPercent = computed(() => {
  if (props.steps.length <= 1) return 100;
  return (props.currentStep / (props.steps.length - 1)) * 100;
});

const checkMobile = (): void => {
  isMobile.value = window.innerWidth <= 768;
  if (!isMobile.value) {
    accordionOpen.value = false;
  }
};

const handleStepClick = (stepIndex: number): void => {
  if (props.allowClick && props.currentStep > stepIndex) {
    emit('step-click', stepIndex);
    accordionOpen.value = false;
  }
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile);
});
</script>

<style scoped>
/* ── Desktop Sidebar ── */
.wizard-sidebar {
  width: 260px;
  flex-shrink: 0;
  background: var(--p-surface-50);
  border-right: 1px solid var(--p-surface-200);
  display: flex;
  flex-direction: column;
  padding: 20px 16px;
  overflow-y: auto;
}

.steps-timeline {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: default;
  transition: background 0.15s, opacity 0.15s;
}

.step-item.step-active {
  background: rgba(var(--app-primary-color-rgb), 0.08);
}

.step-item.step-completed {
  opacity: 0.85;
}

.step-item.step-clickable {
  cursor: pointer;
}

.step-item.step-clickable:hover {
  background: rgba(var(--app-primary-color-rgb), 0.05);
}

.step-marker {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.step-active .step-marker {
  background: var(--app-primary-color);
  color: var(--p-surface-0);
}

.step-completed .step-marker {
  background: rgba(var(--app-primary-color-rgb), 0.15);
  color: var(--app-primary-color);
}

.step-upcoming .step-marker {
  background: var(--p-surface-100);
  color: var(--p-text-muted-color);
}

.step-check {
  font-size: 0.7rem;
}

.step-label {
  font-size: 0.85rem;
  color: var(--app-text-secondary-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.step-active .step-label {
  color: var(--app-primary-color);
  font-weight: 600;
}

.step-upcoming .step-label {
  color: var(--p-text-muted-color);
}

/* ── Sidebar Progress ── */
.sidebar-progress {
  padding: 16px 10px 8px;
  border-top: 1px solid var(--p-surface-200);
  margin-top: 8px;
}

.progress-text {
  font-size: 0.75rem;
  color: var(--app-text-secondary-color);
  margin-bottom: 6px;
}

.progress-bar-track {
  height: 6px;
  background: var(--p-surface-200);
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: var(--app-primary-color);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-percent {
  font-size: 0.7rem;
  color: var(--p-text-muted-color);
  margin-top: 4px;
  text-align: right;
}

/* ── Mobile Accordion ── */
.wizard-accordion {
  border-bottom: 1px solid var(--p-surface-200);
  background: var(--p-surface-50);
}

.accordion-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--app-text-color);
  font-size: 0.9rem;
  font-weight: 500;
  min-height: 48px;
  -webkit-tap-highlight-color: transparent;
}

.accordion-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.accordion-progress-mini {
  width: 40px;
  flex-shrink: 0;
}

.accordion-progress-mini .progress-bar-track {
  height: 4px;
}

.accordion-step-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.accordion-step-count {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
}

.accordion-body {
  padding: 0 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.accordion-step-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
  border-radius: 8px;
  cursor: default;
  transition: background 0.15s;
}

.accordion-step-item.step-active {
  background: rgba(var(--app-primary-color-rgb), 0.08);
}

.accordion-step-item.step-clickable {
  cursor: pointer;
}

.accordion-step-item.step-clickable:hover {
  background: rgba(var(--app-primary-color-rgb), 0.05);
}

.accordion-slide-enter-active,
.accordion-slide-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.accordion-slide-enter-from,
.accordion-slide-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.accordion-slide-enter-to,
.accordion-slide-leave-from {
  opacity: 1;
  max-height: 500px;
}
</style>
